import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/logo";
import { HelpDialog } from "@/components/shell/HelpDialog";
import { LanguageSelector } from "@/components/aegis/LanguageSelector";
import ProfileButton from "@/components/aegis/ProfileButton";
import { MobileBottomNav } from "@/components/shell/MobileBottomNav";
import { useState } from "react";
import { useTranslation } from "@/lib/i18n";
import { useSession } from "@/hooks/useSession";
import { useDemoMode } from "@/hooks/useDemoMode";
import { getEnvironmentState, getEnvironmentLabel } from "@/lib/environment";
import { hasAnyRole, ADMIN_ROLES, CONSUMER_ROLES, PARTNER_ROLES, ENTERPRISE_ROLES, extractUserRoles, type AppRole } from "@/lib/rbac";

const navItems = [
  { href: "/app", labelKey: "nav.dashboard", icon: "dashboard" },
  { href: "/app/assets", labelKey: "nav.assets", icon: "briefcase" },
  { href: "/app/roles", labelKey: "nav.roles", icon: "users" },
  { href: "/app/triggers", labelKey: "nav.triggers", icon: "bell" },
  { href: "/app/instructions", labelKey: "nav.instructions", icon: "file-text" },
  { href: "/app/activity", labelKey: "nav.activity", icon: "activity" },
];

const familyModeItems = [
  { href: "/app/proof-packs", labelKey: "nav.proofPacks", icon: "file-check" },
  { href: "/app/capsules", labelKey: "nav.capsules", icon: "package" },
  { href: "/app/emergency-protocols", labelKey: "nav.protocols", icon: "alert-triangle" },
  { href: "/app/witnesses", labelKey: "nav.witnesses", icon: "user-check" },
];

const elevatedServiceItems = [
  { href: "/app/guardians", labelKey: "nav.guardians", icon: "shield" },
  { href: "/app/digital-custody", labelKey: "nav.digitalCustody", icon: "wallet" },
  { href: "/app/healthcare", labelKey: "nav.healthcare", icon: "heart" },
  { href: "/app/notarization", labelKey: "nav.notarization", icon: "stamp" },
  { href: "/app/beneficiary", labelKey: "nav.beneficiary", icon: "gift" },
];

const trustEngineItems = [
  { href: "/app/trust-network", labelKey: "nav.trustNetwork", icon: "network" },
  { href: "/app/authority-state", labelKey: "nav.authorityState", icon: "shield" },
  { href: "/app/absence-status", labelKey: "nav.absenceStatus", icon: "clock" },
  { href: "/app/compliance", labelKey: "nav.compliance", icon: "scale" },
];

// Demo items - ONLY shown if user explicitly enables demo mode
const demoItems = [
  { href: "/app/plan-demo", labelKey: "nav.planDemo", icon: "presentation" },
  { href: "/app/demo-views", labelKey: "nav.demoViews", icon: "eye" },
];

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const { t } = useTranslation();
  const { signOut, user } = useSession();
  const { enabled: demoModeEnabled, loading: demoModeLoading } = useDemoMode();

  // Determine environment state
  const environmentState = getEnvironmentState(location.pathname, user);
  const environmentLabel = getEnvironmentLabel(environmentState);

  // Determine available zones based on user roles
  const userRoles = (user?.app_metadata?.roles as AppRole[]) || [];
  const availableZones: Array<{zone: string, label: string, path: string}> = [];
  
  if (hasAnyRole(userRoles, CONSUMER_ROLES) || userRoles.length === 0) {
    availableZones.push({ zone: "app", label: "User App", path: "/app" });
  }
  if (hasAnyRole(userRoles, PARTNER_ROLES)) {
    availableZones.push({ zone: "partner", label: "Partner", path: "/partner" });
  }
  if (hasAnyRole(userRoles, ADMIN_ROLES)) {
    availableZones.push({ zone: "admin", label: "Admin", path: "/admin" });
  }
  if (hasAnyRole(userRoles, ENTERPRISE_ROLES)) {
    availableZones.push({ zone: "enterprise", label: "Enterprise", path: "/enterprise" });
  }
  
  const showZoneSwitcher = availableZones.length > 1;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Help Dialog */}
      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
      
      {/* Topbar */}
      <header className="sticky top-0 z-40 h-16 border-b border-border bg-background/80 backdrop-blur-sm">
        <div className="mx-auto flex h-full max-w-[1400px] items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <Logo 
              size="sm" 
              showText={true} 
              subtitle="Digital continuity workspace"
              className="hidden sm:flex"
            />
            <Logo 
              size="sm" 
              showText={false} 
              className="sm:hidden"
            />
            <Badge 
              variant="secondary" 
              className="ml-2 hidden sm:inline-flex animate-pulse bg-green-100 text-green-800 border-green-200"
            >
              {environmentLabel}
            </Badge>
          </div>

          <div className="flex items-center gap-2">
            <LanguageSelector />
            <Button 
              variant="ghost" 
              size="sm" 
              className="gap-2"
              onClick={() => setHelpOpen(true)}
            >
              <span>{t('nav.help')}</span>
            </Button>
            <ProfileButton />
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1400px]">
        {/* Sidebar */}
        <aside
          className="hidden h-[calc(100vh-64px)] w-60 shrink-0 border-r border-border bg-muted/30 md:block"
        >
          <nav className="flex flex-col h-full p-3">
            {/* Zone switcher for multi-role users */}
            {showZoneSwitcher && (
              <div className="mb-3 pb-3 border-b border-border">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 px-2">
                  Switch Zone
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {availableZones.map((z) => (
                    <button
                      key={z.zone}
                      onClick={() => navigate(z.path)}
                      className={cn(
                        "px-2 py-1.5 text-xs font-medium rounded-md transition-colors",
                        location.pathname.startsWith(z.path)
                          ? "bg-accent text-accent-foreground"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      {z.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex items-center justify-between mb-2 px-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('nav.navigation')}
              </p>
            </div>

            <ul className="space-y-1 flex-1">
              {navItems.map((item) => {
                const active = location.pathname === item.href ||
                  (item.href !== "/app" && location.pathname.startsWith(item.href));
                return (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className={cn(
                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                        active
                          ? "bg-accent text-accent-foreground font-medium"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <span>{t(item.labelKey)}</span>
                    </Link>
                  </li>
                );
              })}

              {/* Divider */}
              <li className="py-2">
                <div className="h-px bg-border" />
              </li>

              {/* Family Mode items */}
              <li className="px-2 pt-1 pb-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('nav.familyMode')}
                </p>
              </li>
              {familyModeItems.map((item) => {
                const active = location.pathname === item.href ||
                  (item.href !== "/app" && location.pathname.startsWith(item.href));
                return (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className={cn(
                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                        active
                          ? "bg-accent text-accent-foreground font-medium"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <span>{t(item.labelKey)}</span>
                    </Link>
                  </li>
                );
              })}

              {/* Divider */}
              <li className="py-2">
                <div className="h-px bg-border" />
              </li>

              {/* Elevated Services items */}
              <li className="px-2 pt-1 pb-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('nav.elevatedServices')}
                </p>
              </li>
              {elevatedServiceItems.map((item) => {
                const active = location.pathname === item.href ||
                  (item.href !== "/app" && location.pathname.startsWith(item.href));
                return (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className={cn(
                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                        active
                          ? "bg-accent text-accent-foreground font-medium"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <span>{t(item.labelKey)}</span>
                    </Link>
                  </li>
                );
              })}

              {/* Divider */}
              <li className="py-2">
                <div className="h-px bg-border" />
              </li>

              {/* Trust Engine items */}
              <li className="px-2 pt-1 pb-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('nav.trustEngine')}
                </p>
              </li>
              {trustEngineItems.map((item) => {
                const active = location.pathname === item.href ||
                  (item.href !== "/app" && location.pathname.startsWith(item.href));
                return (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className={cn(
                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                        active
                          ? "bg-accent text-accent-foreground font-medium"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      <span>{t(item.labelKey)}</span>
                    </Link>
                  </li>
                );
              })}

              {/* Conditional Admin link - ONLY shown if user is platform admin */}
              {(() => {
                const userRoles = extractUserRoles(user);
                const isAdmin = userRoles.includes('platform_admin');
                return isAdmin && (
                  <>
                    {/* Divider */}
                    <li className="py-2">
                      <div className="h-px bg-border" />
                    </li>

                    {/* Admin section */}
                    <li className="px-2 pt-1 pb-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Administration
                      </p>
                    </li>
                    <li>
                      <Link
                        to="/admin"
                        className={cn(
                          "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                          location.pathname.startsWith("/admin")
                            ? "bg-accent text-accent-foreground font-medium"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        )}
                      >
                        <span>Admin Panel</span>
                      </Link>
                    </li>
                  </>
                );
              })()}

              {/* Conditional Demo items - ONLY shown if demo mode enabled */}
              {!demoModeLoading && demoModeEnabled && (
                <>
                  {/* Divider */}
                  <li className="py-2">
                    <div className="h-px bg-border" />
                  </li>

                  {/* Demo items section */}
                  <li className="px-2 pt-1 pb-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Demo Mode
                    </p>
                  </li>
                  {demoItems.map((item) => {
                    const active = location.pathname === item.href ||
                      (item.href !== "/app" && location.pathname.startsWith(item.href));
                    return (
                      <li key={item.href}>
                        <Link
                          to={item.href}
                          className={cn(
                            "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                            active
                              ? "bg-accent text-accent-foreground font-medium"
                              : "text-muted-foreground hover:bg-muted hover:text-foreground"
                          )}
                        >
                          <span>{t(item.labelKey)}</span>
                        </Link>
                      </li>
                    );
                  })}
                </>
              )}
              {/* Demo items section end */}
            </ul>

            {/* Footer */}
            <div className="border-t border-border pt-2 mt-2 space-y-1">
              <Link
                to="/app/settings"
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <span>{t('nav.settings')}</span>
              </Link>
              <button
                onClick={signOut}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <span>{t('auth.signOut')}</span>
              </button>
            </div>
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto pb-20">
          <div className="mx-auto max-w-[1000px] p-4 md:p-6">{children}</div>
        </main>
      </div>
      <MobileBottomNav />
    </div>
  );
}

export default AppShell;
